import '../../motion.css';
import './build.css';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { buildExperienceCopy, type BuildExperienceCopyKey } from '@/i18n/ns/build-experience';
import { OriginTag } from '@/components/ui/kit';
import { stagger, useEnterOnce } from '@/lib/motion';
import {
  COUNTED,
  PHASE_COPY,
  SLOW_MS,
  clock,
  deriveLine,
  lastStepParts,
  type LineView,
} from './line-model';
import type { BuildBudget, BuildLineVM, BuildSource, BuildWait, DisplayAction } from './vm';
import { waitLine, type WaitLine } from './wait-line';

const ACTION_COPY: Partial<Record<DisplayAction['name'], BuildExperienceCopyKey>> = {
  stop: 'stop',
  continue: 'continue',
  fix: 'fix',
  'handoff-chat': 'fixInChat',
};

/** A clock that ticks once a second while `running`. */
export function useNow(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  return now;
}

/** The check that draws itself, with one soft ring, when `play`; otherwise it is simply there. */
export function DrawnCheck({ size = 16, play = true }: { size?: number; play?: boolean }) {
  return (
    <span className="pc-build-check" aria-hidden="true" style={{ width: size, height: size }}>
      {play && <span className="pc-build-check-ring pc-m-ring" />}
      <svg
        className={play ? 'pc-m-checkdraw' : undefined}
        viewBox="0 0 16 16"
        width={size}
        height={size}
      >
        <circle cx="8" cy="8" r="7.25" pathLength={1} className="pc-build-check-circle" />
        <path d="M4.75 8.25 7 10.5l4.25-4.75" pathLength={1} />
      </svg>
    </span>
  );
}

type Props = {
  vm: BuildLineVM;
  /** pending-contract: origin, start time and labels (see BuildSource). */
  source?: BuildSource | null;
  /** pending-contract: the budget reservation's display amounts, for the budget pause. */
  budget?: BuildBudget | null;
  /** pending-contract (T1-SES): the wait while the model is busy (see BuildWait). */
  wait?: BuildWait | null;
  /** The kind word for "{kind} is ready", from the kind registry's copy. */
  kindLabel?: string | null;
  phone?: boolean;
  /** Resolves ART's i18n keys: action labels, disabled reasons, the failure's issue sentence. */
  labelFor?: (key: string) => string;
  onAction: (action: DisplayAction) => void;
  /** Opens the run record behind the origin tag, when there is one to open. */
  onOpenRun?: () => void;
  /** Test seam: a fixed clock. */
  now?: number;
};

type Copy = Record<BuildExperienceCopyKey, string>;

function sentence(
  view: LineView,
  copy: Copy,
  source: BuildSource | null,
  budget: BuildBudget | null,
): string {
  const step = view.phase ? copy[PHASE_COPY[view.phase]] : copy.loading;
  switch (view.status) {
    case 'denied':
      return copy.denied;
    case 'loading':
      return copy.loading;
    case 'blocked':
      if (view.blockedCopy === 'blockedBudget' && budget)
        return formatCopy(copy.budgetPaused, { spent: budget.spent, limit: budget.limit });
      return copy[view.blockedCopy ?? 'blockedOther'];
    case 'failed':
      return copy.failed;
    case 'stopped':
      return copy.stopped;
    case 'stopping':
      return copy.stopping;
    case 'unknown':
      return copy.outcomeUnknown;
    case 'confirming':
      return copy.confirming;
    default:
      // An outside agent's phases are never narrated; it is named, or neutrally "the connected agent".
      if (view.remote) {
        const agent = source?.agentLabel ?? '';
        return agent ? formatCopy(copy.remoteWorking, { agent }) : copy.remoteWorkingUnnamed;
      }
      return step;
  }
}

function waitApplies(wait: BuildWait, status: LineView['status']): boolean {
  return wait.kind === 'stopped' ? status === 'stopped' : status === 'running' || status === 'blocked';
}

/** The origin tag: who builds, from the run record. A native build names its model ("Built
 * with …"); a new version from "Try with another model" says so in the present while it runs
 * ("Building with …"), so the model actually running is never in doubt. */
function originLabel(
  source: BuildSource | null,
  remote: boolean,
  active: boolean,
  copy: Copy,
): string | null {
  if (source?.publishedByAgent && source.agentLabel)
    return formatCopy(copy.publishedBy, { agent: source.agentLabel });
  if (remote && source?.agentLabel) return formatCopy(copy.byAgent, { agent: source.agentLabel });
  // The registry's full name; a plain label until ART projects the registry presentation.
  const name = source?.model?.fullName ?? source?.modelLabel;
  if (!source || !name) return null;
  const newVersion = active && source.version?.kept != null;
  return formatCopy(newVersion ? copy.buildingWith : copy.builtWith, { model: name });
}

/**
 * The busy wait's second line and its quiet actions (the slow line's style, #3291). The
 * countdown changes every second, so it stays outside the live region; the title announces.
 */
function WaitNote({
  line,
  stop,
  onAction,
  onKeepWaiting,
}: {
  line: WaitLine;
  stop: ReactNode;
  onAction: (action: DisplayAction) => void;
  onKeepWaiting: () => void;
}) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  // "Keep waiting" says yes once. The note is keyed by the wait, so a new wait asks again.
  const [kept, setKept] = useState(false);
  const keep = line.keepWaiting && !kept;
  const alternative = line.alternative;
  if (!line.note && !line.retry && !keep && !alternative && !stop) return null;
  return (
    <div className="pc-build-note" data-testid="build-wait" role="group">
      {line.note && <span data-testid="build-wait-note">{line.note}</span>}
      {(line.retry || keep || alternative || stop) && (
        <span className="pc-build-actions">
          {line.retry && (
            <button
              type="button"
              className="pc-btn pc-build-action"
              data-variant="quiet"
              data-action="retry"
              disabled={line.retry.action.status !== 'available'}
              aria-busy={line.retry.action.status === 'pending' || undefined}
              onClick={() =>
                line.retry?.action.status === 'available' && onAction(line.retry.action)
              }
            >
              {line.retry.label}
            </button>
          )}
          {keep && (
            <button
              type="button"
              className="pc-btn pc-build-action"
              data-variant="quiet"
              data-action="keep-waiting"
              onClick={() => {
                setKept(true);
                onKeepWaiting();
              }}
            >
              {copy.keepWaiting}
            </button>
          )}
          {alternative &&
            ('action' in alternative ? (
              <button
                type="button"
                className="pc-btn pc-build-action"
                data-variant="quiet"
                data-action="try-with"
                disabled={alternative.action.status !== 'available'}
                aria-busy={alternative.action.status === 'pending' || undefined}
                onClick={() =>
                  alternative.action.status === 'available' && onAction(alternative.action)
                }
              >
                {alternative.label}
              </button>
            ) : (
              <span className="pc-build-muted" data-testid="build-alt-reason">
                {alternative.reason}
              </span>
            ))}
          {stop}
        </span>
      )}
    </div>
  );
}

/**
 * The build line (transcript) and, with `variant="bar"`, the build bar along the top of the
 * canvas. One component, so the chat and the canvas never disagree about a build.
 * Everything comes from the display VM; the clock is the only thing the line adds.
 *
 * Layout: the sentence takes the room it needs; the tail (last step, clock, Stop) sits at the
 * line's end, and on a narrow line wraps onto its own row.
 */
export function BuildLine({
  vm,
  source = null,
  budget = null,
  wait = null,
  kindLabel = null,
  phone = false,
  labelFor,
  onAction,
  onOpenRun,
  now: fixedNow,
  variant = 'line',
}: Props & { variant?: 'line' | 'bar' }) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  const ticking =
    (vm.state.status === 'ready' && vm.state.data.phase !== 'settled') ||
    wait?.kind === 'admission';
  const liveNow = useNow(ticking && fixedNow === undefined);
  const now = fixedNow ?? liveNow;
  const view = deriveLine(vm, source, now);
  // The busy wait speaks for the line until the build is done, refused or failed.
  const jobKey = vm.scope.jobId ?? vm.scope.artifactId ?? '';
  // The VM's own state dominates: a wait shows only while the VM is waitable (running or
  // paused; stopped for a stopped wait). Uncertainty, confirming, stopping and every settled
  // state keep their own words and actions, and a stale wait never revives a new start.
  const waiting = wait && waitApplies(wait, view.status) ? waitLine(wait, copy, now, vm.locale) : null;
  // One wait of one job: a new job or a new wait asks Keep waiting again.
  const waitKey = wait
    ? `${jobKey}:${wait.id ?? ''}:${wait.kind}:${'step' in wait ? wait.step : ''}`
    : '';
  const firstSight = useEnterOnce(`build-line:${jobKey}:${variant}`);
  // The check draws once, the first time this build is seen done, not on every remount.
  const firstDone =
    useEnterOnce(`build-done:${jobKey}:${view.status === 'done'}`) && view.status === 'done';
  const root = useRef<HTMLDivElement>(null);
  // "Keep waiting" quiets the 30 s choice until the build reports again, or for another 30 s.
  const [waitedAt, setWaitedAt] = useState<number | null>(null);
  const slowShown =
    view.beat === 'slow' &&
    (waitedAt === null || view.quietMs < waitedAt || view.quietMs >= waitedAt + SLOW_MS);
  useEffect(() => {
    if (view.beat === 'live') setWaitedAt(null);
  }, [view.beat]);

  const running = !waiting && (view.status === 'running' || view.status === 'stopping');
  const settledOrPaused =
    Boolean(waiting) ||
    view.status === 'blocked' ||
    view.status === 'unknown' ||
    view.status === 'failed';
  const label = (action: DisplayAction) => {
    // Where the line stops for a person, Stop means "stop here": keep what is saved.
    if (action.name === 'stop' && settledOrPaused) return copy.stopHere;
    if (action.name === 'continue' && view.blockedCopy === 'blockedBudget' && budget?.more)
      return formatCopy(copy.continueWith, { amount: budget.more });
    // pending-contract: ART's reconcile action for an unknown outcome is projected as 'refresh'.
    if (action.name === 'refresh' && view.status === 'unknown') return copy.checkAgain;
    const own = ACTION_COPY[action.name];
    return own ? copy[own] : (labelFor?.(action.labelKey) ?? action.labelKey);
  };
  const button = (action: DisplayAction, primary = false) => (
    <button
      key={action.id}
      type="button"
      className="pc-btn pc-build-action"
      data-variant={primary ? 'primary' : 'quiet'}
      data-action={action.name}
      disabled={action.status !== 'available'}
      aria-busy={action.status === 'pending' || undefined}
      title={action.status === 'disabled' ? (labelFor?.(action.reasonKey) ?? undefined) : undefined}
      onClick={() => action.status === 'available' && onAction(action)}
    >
      {action.name === 'stop' && action.status === 'pending' ? copy.stopping : label(action)}
    </button>
  );

  const step = view.phase ? copy[PHASE_COPY[view.phase]] : null;
  // The 30 s choice carries Stop; while it shows, the tail does not repeat it.
  const slowNote = running && !view.remote && slowShown && Boolean(step);
  const headStop = slowNote ? null : view.stop;
  const remoteLast = view.remote
    ? lastStepParts(view.quietMs, Boolean(vm.state.status === 'ready' && vm.state.data.lastSeenAt))
    : null;
  // Still building: running or waiting. A stopped wait has stopped building.
  const building = running || (Boolean(waiting) && wait?.kind !== 'stopped');
  const origin = originLabel(source, view.remote, building, copy);
  // Inline lines use the registry's short name (DES1 #3431 P3); the origin tag keeps the full one.
  const inlineName = source?.model?.shortName ?? source?.modelLabel ?? null;
  // A new version from the same request: which one this is, and that the last one is kept.
  const version =
    building && !view.remote && inlineName && source?.version?.kept != null
      ? `${formatCopy(copy.buildingVersion, { n: String(source.version.n), model: inlineName })} · ${formatCopy(copy.versionKept, { kept: String(source.version.kept) })}`
      : null;
  // The failure's own reason, from ART's issue-code sentence (B12), when there is one.
  const reason =
    view.status === 'failed' && vm.state.status === 'error' && labelFor
      ? labelFor(vm.state.messageKey)
      : null;
  // The settled and paused states end with their actions; Stop joins them there.
  // While it waits, the wait's own row carries the actions; a stopped wait keeps Continue.
  const endActions =
    waiting && wait?.kind !== 'stopped'
      ? []
      : settledOrPaused || view.status === 'stopped'
        ? [...view.actions, ...(settledOrPaused && view.stop && !waiting ? [view.stop] : [])]
        : [];

  const glyph = waiting ? (
    waiting.dot === 'live' ? (
      <span className="pc-build-dot pc-m-breathe" aria-hidden="true" />
    ) : (
      // Waiting: the live dot's ring, still, without its pulse.
      <span
        className="pc-build-dot"
        data-tone={waiting.dot === 'ring' ? 'waiting' : 'quiet'}
        aria-hidden="true"
      />
    )
  ) : view.status === 'done' ? (
    <DrawnCheck play={firstDone} />
  ) : running || view.status === 'loading' || view.status === 'confirming' ? (
    <span className="pc-build-dot pc-m-breathe" aria-hidden="true" />
  ) : (
    <span
      className="pc-build-dot"
      data-tone={view.status === 'failed' ? 'problem' : 'quiet'}
      aria-hidden="true"
    />
  );

  const title = waiting
    ? waiting.title
    : view.status === 'done'
      ? source?.publishedByAgent && source.agentLabel
        ? formatCopy(copy.publishedBy, { agent: source.agentLabel })
        : kindLabel
          ? formatCopy(copy.ready, { kind: kindLabel })
          : copy.readyGeneric
      : sentence(view, copy, source, budget);

  return (
    <div
      className="pc-build-line"
      data-variant={variant}
      data-status={view.status}
      data-wait={waiting ? wait?.kind : undefined}
      data-beat={view.beat}
      data-phone={phone ? 'true' : undefined}
      data-testid={variant === 'bar' ? 'build-bar' : 'build-line'}
      ref={root}
      tabIndex={-1}
    >
      {variant === 'bar' && running && (
        <span className="pc-build-rail pc-m-glide" aria-hidden="true" />
      )}
      <div className="pc-build-main">
        <span className="pc-build-lead">
          {glyph}
          <span className="pc-build-text" role="status" aria-live="polite">
            <span
              className={running && !view.remote ? 'pc-build-title pc-m-glint' : 'pc-build-title'}
            >
              {title}
            </span>
            {!phone && running && view.status === 'running' && view.position !== null && !view.remote && (
              <span className="pc-build-meta">
                {formatCopy(copy.stepOf, {
                  n: String(view.position),
                  total: String(COUNTED.length),
                })}
              </span>
            )}
          </span>
        </span>
        {/* The tail sits at the line's end. Per-second text stays outside the live region. */}
        {running && (remoteLast || view.elapsedMs !== null || headStop) && (
          <span className="pc-build-tail">
            {remoteLast && (
              <span className="pc-build-meta" data-testid="build-last-step">
                {formatCopy(copy[remoteLast.key], remoteLast.values)}
              </span>
            )}
            {view.elapsedMs !== null && (
              <span className="pc-build-clock">
                <span className="sr-only">{copy.elapsed} </span>
                {clock(view.elapsedMs)}
              </span>
            )}
            {headStop && button(headStop)}
          </span>
        )}
      </div>

      {version && (
        <p className="pc-build-note" data-testid="build-version">
          {version}
        </p>
      )}
      {reason && (
        <p className="pc-build-note" data-testid="build-reason">
          {reason}
        </p>
      )}

      {variant === 'bar' && !phone && !view.remote && view.steps.length > 0 && (
        <ol className="pc-build-steps" aria-label={copy.steps}>
          {view.steps.map((s, i) => (
            <li
              key={s.phase}
              data-state={s.state}
              className={firstSight && s.state === 'done' ? 'pc-m-fade' : undefined}
              style={firstSight ? stagger(i) : undefined}
            >
              {s.state === 'done' && (
                <CheckIcon size={12} aria-hidden className="pc-build-step-check" />
              )}
              {copy[PHASE_COPY[s.phase]]}
            </li>
          ))}
        </ol>
      )}

      {running && !view.remote && view.beat === 'still' && step && (
        <p className="pc-build-note pc-m-fade" data-testid="build-still" role="status">
          {formatCopy(copy.stillWorking, { step: step.toLocaleLowerCase(vm.locale) })}
        </p>
      )}
      {slowNote && step && (
        <div className="pc-build-note pc-m-fade" data-testid="build-slow" role="group">
          <span role="status">{formatCopy(copy.slowReason, { step })}</span>
          {/* The same actions row as "Stop here", so the choice reads as controls, not as text. */}
          <span className="pc-build-actions">
            <button
              type="button"
              className="pc-btn pc-build-action"
              data-variant="quiet"
              onClick={() => {
                setWaitedAt(view.quietMs);
                // The button leaves with the note; keep focus on the line, not the page.
                root.current?.focus();
              }}
            >
              {copy.keepWaiting}
            </button>
            {view.stop && button(view.stop)}
          </span>
        </div>
      )}
      {waiting && wait?.kind !== 'stopped' && (
        <WaitNote
          key={waitKey}
          line={waiting}
          stop={waiting.stop && view.stop ? button(view.stop) : null}
          onAction={onAction}
          onKeepWaiting={() => root.current?.focus()}
        />
      )}
      {endActions.length > 0 && (
        <div className="pc-build-actions">
          {endActions.map((a, i) => button(a, i === 0 && a.name !== 'stop'))}
        </div>
      )}
      {origin &&
        view.status !== 'denied' &&
        !(view.status === 'done' && source?.publishedByAgent) && (
          <span
            className="pc-build-origin"
            title={view.remote ? copy.agentNameReported : undefined}
          >
            <OriginTag label={origin} onOpen={onOpenRun} />
          </span>
        )}
    </div>
  );
}

/** The build bar: the same line, laid along the top of the canvas with its step track. */
export function BuildBar(props: Props) {
  return <BuildLine {...props} variant="bar" />;
}
